import { gradientScaleCss, temperatureColorScale } from 'utils/colors/colorScale.utils';
import { formatDateTime } from 'utils/dates/date.utils';
import { useId } from 'react';

const temperatureStops = [...temperatureColorScale.domain()];
const minimumTemperature = temperatureStops[0];
const maximumTemperature = temperatureStops[temperatureStops.length - 1];
const temperatureRange = maximumTemperature - minimumTemperature;

// Converts a temperature value to a percentage position along the temperature gradient
const getTemperatureStopPosition = (temperature) =>
  ((temperature - minimumTemperature) / temperatureRange) * 100;

const temperatureGradient = gradientScaleCss(temperatureColorScale);

// Single source for both the visible threshold label and its screen-reader wording.
const getTemperatureStopLabels = (temperature, index) => {
  if (index === 0) {
    return {
      label: `≤${temperature}°`,
      ariaLabel: `At or below ${temperature} degrees Fahrenheit`,
    };
  }

  if (index === temperatureStops.length - 1) {
    return {
      label: `≥${temperature}°`,
      ariaLabel: `At or above ${temperature} degrees Fahrenheit`,
    };
  }

  return { label: `${temperature}°`, ariaLabel: `${temperature} degrees Fahrenheit` };
};

const TemperatureLegend = ({ latestObservationTime, compact = false }) => {
  const scaleNoteId = useId();
  const visibleTemperatureStops = compact
    ? temperatureStops.filter((_, index) => index % 2 === 0)
    : temperatureStops;

  return (
    <figure
      aria-describedby={scaleNoteId}
      aria-label="Current temperature color scale in degrees Fahrenheit"
      className={`current-conditions-temperature-legend w-100 ${
        compact ? 'current-conditions-temperature-legend-compact' : ''
      }`}
    >
      <figcaption className={`${compact ? 'mb-1' : 'mb-2'} text-center`}>
        <span className="fw-semibold">
          {compact ? 'Temperatures (°F)' : 'Current temperatures (°F)'}
        </span>
        {latestObservationTime && (
          <span className={compact ? 'current-conditions-temperature-observation d-block' : ''}>
            {compact ? 'Latest observation ' : ' · Latest observation '}
            {formatDateTime(latestObservationTime)}
          </span>
        )}
        <small className={compact ? 'visually-hidden' : 'd-block text-secondary'} id={scaleNoteId}>
          Endpoint colors include temperatures beyond the labeled thresholds.
        </small>
      </figcaption>
      <div
        aria-hidden="true"
        className="current-conditions-temperature-gradient rounded-2"
        style={{ backgroundImage: temperatureGradient }}
      />
      <ul
        aria-label="Temperature color thresholds"
        className="current-conditions-temperature-labels m-0 list-unstyled"
      >
        {visibleTemperatureStops.map((temperature) => {
          const index = temperatureStops.indexOf(temperature);
          const { label, ariaLabel } = getTemperatureStopLabels(temperature, index);

          return (
            <li
              aria-label={ariaLabel}
              key={temperature}
              style={{
                '--current-conditions-temperature-position': `${getTemperatureStopPosition(temperature)}%`,
              }}
            >
              {label}
            </li>
          );
        })}
      </ul>
    </figure>
  );
};

export default TemperatureLegend;
